import { AxiosError } from 'axios';
import api from '../api/apiClient';

export type NotificationType =
    // Appointment notifications
    | 'appointment_reminder'
    | 'appointment_reminder_24h'
    | 'appointment_reminder_2h'
    | 'appointment_booked'
    | 'appointment_confirmed'
    | 'appointment_cancelled'
    | 'appointment_modified'
    | 'appointment_checkin'
    | 'appointment_started'
    | 'appointment_completed'
    | 'appointment_noshow'
    | 'appointment_rescheduled'
    | 'appointment_expired'
    | 'appointment_overdue_completion_reminder'
    // Payment notifications
    | 'payment_received'
    | 'payment_failed'
    // Review notifications
    | 'review_received'
    // System notifications
    | 'profile_update'
    | 'service_update'
    | 'system_notification'
    // Salon notifications
    | 'salon_invitation_received'
    | 'salon_invitation_accepted'
    | 'salon_invitation_declined'
    // Affiliate Commission notifications
    | 'commission_created'
    | 'commission_approved'
    | 'commission_cancelled'
    | 'commission_paid'
    | 'commission_disputed'
    // Affiliate Dispute notifications
    | 'dispute_resolved'
    | 'dispute_rejected'
    // Affiliate Payout notifications
    | 'payout_requested'
    | 'payout_processing'
    | 'payout_completed'
    | 'payout_failed'
    // Affiliate Tier & Performance notifications
    | 'tier_upgraded'
    | 'tier_downgraded'
    | 'milestone_reached'
    | 'bonus_earned'
    // Admin Actions on Affiliates
    | 'affiliate_approved'
    | 'affiliate_rejected'
    | 'affiliate_suspended'
    | 'affiliate_reactivated';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';
export type NotificationStatus = 'unread' | 'read' | 'archived';
export type DeliveryMethod = 'in-app' | 'email' | 'sms' | 'push';

export interface NotificationAction {
    type: 'link' | 'button' | 'deep_link';
    text: string;
    url: string;
}

export interface NotificationData {
    appointmentId?: string;
    serviceId?: string;
    reviewId?: string;
    paymentId?: string;
    // Affiliate-specific data
    commissionId?: string;
    payoutId?: string;
    affiliateId?: string;
    disputeId?: string;
    additionalData?: Record<string, any>;
    stylistId?: string;
}

export interface DeliveryStatus {
    inApp?: { sent: boolean; sentAt?: string };
    email?: { sent: boolean; sentAt?: string };
    sms?: { sent: boolean; sentAt?: string };
    push?: { sent: boolean; sentAt?: string };
}

export interface Notification {
    _id: string;
    user: string;
    type: NotificationType;
    title: string;
    message: string;
    data?: NotificationData;
    priority: NotificationPriority;
    status: NotificationStatus;
    deliveryMethod: DeliveryMethod[];
    deliveryStatus: DeliveryStatus;
    expiresAt?: string;
    action?: NotificationAction;
    createdAt: string;
    updatedAt: string;
}

export interface NotificationPreferences {
    email: { enabled: boolean; settings: { bookings: boolean; reminders: boolean; marketing: boolean } };
    sms:   { enabled: boolean; settings: { bookings: boolean; reminders: boolean; marketing: boolean } };
    push:  { enabled: boolean; settings: { bookings: boolean; reminders: boolean; marketing: boolean } };
}

export interface NotificationStats {
    _id: NotificationType;
    count: number;
    read: number;
    unread: number;
}

export interface GetNotificationsParams {
    page?: number;
    limit?: number;
    status?: NotificationStatus;
}

export interface GetNotificationsResponse {
    success: boolean;
    data: Notification[];
    count: number;
    total: number;
    page: number;
    pages: number;
    /** Present when API returns aggregate unread count (matches DB). */
    unreadCount?: number;
}

/**
 * Fetch paginated notifications.
 * GET /notifications
 */
export async function getNotifications(
    params: GetNotificationsParams = {}
): Promise<GetNotificationsResponse> {
    try {
        const response = await api.notification.getNotifications(params);
        const responseData = response.data;
        
        // Handle different response structures
        let data: Notification[];
        let count: number;
        let total: number;
        let page: number;
        
        if (Array.isArray(responseData)) {
            // If response is directly an array
            data = responseData;
            count = data.length;
            total = data.length;
            page = params.page || 1;
        } else if (responseData.data) {
            // If response has nested data
            data = responseData.data;
            count = responseData.count || data.length;
            total = responseData.total || data.length;
            page =
                responseData.pagination?.page ??
                responseData.page ??
                params.page ??
                1;
        } else {
            // Fallback
            data = [];
            count = 0;
            total = 0;
            page = params.page || 1;
        }
        
        const pages =
            responseData?.pagination?.pages ??
            Math.ceil(total / (params.limit || 10));
        const unreadCountFromApi =
            typeof responseData?.unreadCount === 'number'
                ? responseData.unreadCount
                : undefined;
        return {
            success: true,
            data,
            count,
            total,
            page,
            pages,
            ...(unreadCountFromApi != null ? { unreadCount: unreadCountFromApi } : {}),
        };
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error fetching notifications:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Mark notifications as read.
 * PUT /notifications/read
 */
export async function markAsRead(notificationIds: string[]): Promise<void> {
    try {
        await api.notification.markAsRead(notificationIds);
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error marking notifications as read:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Delete a single notification.
 * DELETE /notifications/:notificationId
 */
export async function deleteNotification(notificationId: string): Promise<void> {
    try {
        await api.delete(`/notifications/${notificationId}`);
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error(`Error deleting notification ${notificationId}:`, err.response?.data || err.message);
        throw err;
    }
}

/**
 * Update user notification preferences.
 * PUT /notifications/preferences
 */
export async function updatePreferences(
    preferences: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
    try {
        const response = await api.put('/notifications/preferences', preferences);
        return response.data.data;
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error updating preferences:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Get user notification preferences.
 * GET /notifications/settings
 */
export async function getSettings(): Promise<NotificationPreferences> {
    try {
        const response = await api.get('/notifications/settings');
        return response.data.data;
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error fetching settings:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Get notification statistics.
 * GET /notifications/stats
 */
export async function getNotificationStats(): Promise<NotificationStats[]> {
    try {
        const response = await api.get('/notifications/stats');
        return response.data.data;
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error fetching stats:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Send a reminder notification.
 * POST /notifications/reminders/:appointmentId
 */
export async function sendReminder(
    appointmentId: string,
    type: '24h_before' | '2h_before'
): Promise<void> {
    try {
        await api.post(`/notifications/reminders/${appointmentId}`, { type });
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error sending reminder:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Get reminder preferences.
 * GET /notifications/reminder-preferences
 */
export async function getReminderPreferences(): Promise<{ email: boolean; sms: boolean; push: boolean }> {
    try {
        const response = await api.get('/notifications/reminder-preferences');
        return response.data.data;
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error fetching reminder preferences:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Update reminder preferences.
 * PUT /notifications/reminder-preferences
 */
export async function updateReminderPreferences(
    preferences: { email: boolean; sms: boolean; push: boolean }
): Promise<void> {
    try {
        await api.put('/notifications/reminder-preferences', preferences);
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error updating reminder preferences:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Bulk delete notifications older than a specified date.
 * POST /notifications/bulk-delete
 */
export async function bulkDelete(
    params: { olderThan?: Date } = {}
): Promise<void> {
    try {
        await api.post('/notifications/bulk-delete', params);
    } catch (error) {
        const err = error as AxiosError<any>;
        console.error('Error bulk deleting notifications:', err.response?.data || err.message);
        throw err;
    }
}

/**
 * Utility: check if a notification is expired.
 */
export function isExpired(notification: Notification): boolean {
    if (!notification.expiresAt) return false;
    return new Date(notification.expiresAt) < new Date();
}

/**
 * Utility: count unread notifications.
 */
export function getUnreadCount(notifications: Notification[]): number {
    return notifications.filter(n => n.status === 'unread').length;
}

/**
 * Utility: format time ago string for notifications.
 */
export function formatNotificationTime(notification: Notification): string {
    const date = new Date(notification.createdAt);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
}
